import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Detect Remittance Collection Threads Step
 *
 * Runs all three advice-scoped collection-thread detections in one pass over the
 * remittance advice's line items:
 *   - short-pay   — known invoice net-applied SHORT of its balance
 *   - over-payment — known invoice net-applied OVER its balance
 *   - deduction   — unknown-entity lines (no invoice) rolled into one thread
 *
 * Each sub-detection reads its own org config flag and no-ops when disabled, so
 * this single node replaces wiring three separate detection steps. All three are
 * idempotent (partial unique indexes / metadata dedup), so a retry is safe.
 */
export const detectRemittanceCollectionThreadsStep = defineStepConfig({
  type: "detect_remittance_collection_threads",
  name: "Detect Remittance Collection Threads",
  description:
    "Open short_pay, over_payment, and deduction collection threads from a remittance advice in a single pass (each gated by its own org config).",
  category: "Data Access",
  libraryModule: "AccountsReceivable",
  inputSchema: z.object({
    remittanceAdviceId: z
      .uuid()
      .describe(
        "Remittance advice whose line items are checked for short pays, over-payments, and deductions",
      ),
  }),
  outputSchema: z.object({
    shortPay: z.object({
      created: z.int().nonnegative(),
      skipped: z.int().nonnegative(),
      failed: z.int().nonnegative(),
    }),
    overPayment: z.object({
      created: z.int().nonnegative(),
      skipped: z.int().nonnegative(),
      failed: z.int().nonnegative(),
    }),
    deduction: z.object({
      created: z.int().nonnegative(),
      skipped: z.int().nonnegative(),
      failed: z.int().nonnegative(),
    }),
    totalCreated: z.int().nonnegative(),
  }),
  configSchema: z.object({}),
  emits: [] as const,
});
